/**
 * De regels van een inkooporder in de aanmaakwizard: één plek die bepaalt hoe een regel eruitziet,
 * of je nu naar stap 2 gaat, een concept opent of opslaat vanuit stap 1.
 *
 * Waarom dit eruit is gehaald (22 oktober 2026, IO-20261022-85): "Opslaan als concept" in stap 1
 * schreef regels met alleen een product-id. De prijs werd 0, inner en master werden leeg, want die
 * vult stap 2 pas in uit de catalogus. Opende je dat concept daarna met "Bewerken", dan ging de
 * wizard meteen naar stap 2 en nam hij die lege waarden letterlijk over. De aanvulling uit de
 * catalogus gebeurde alleen voor regels die nieuw in stap 2 kwamen, dus alles bleef leeg.
 */

// Een prijs of verpakkingsaantal van 0 is nooit een echte keuze: versturen naar de leverancier
// eist voor alle drie > 0 (zie `incompleteLines` in de wizard). Leeg of 0 betekent dus "nog niet
// ingevuld", en dan geldt de catalogus.
const filled = (value) => Number(value) > 0;

/** Vult de lege prijs en verpakking van een gewone regel aan uit de catalogus. Ingevulde waarden blijven staan. */
export const fillLineFromCatalog = (row, product) => {
  if (!row || row.isDraft || !product) return row;
  return {
    ...row,
    price: filled(row.price) ? row.price : product.purchasePrice,
    innerPackagingQuantity: filled(row.innerPackagingQuantity)
      ? row.innerPackagingQuantity
      : product.innerPackagingQuantity,
    masterPackagingQuantity: filled(row.masterPackagingQuantity)
      ? row.masterPackagingQuantity
      : product.masterPackagingQuantity,
  };
};

/**
 * De regels die horen bij de selectie van stap 1: één per aangevinkt product en één per
 * productconcept, in die volgorde. Wat er al stond, houdt zijn ingevulde waarden (aantal,
 * prioriteit). Nieuwe regels en lege velden krijgen de waarden uit de catalogus. Wat in stap 1
 * niet meer aangevinkt is, valt weg.
 */
export const buildLinesForSelection = ({ existingLines, selectedProductIds, draftProducts, products }) => {
  const existingByProduct = {};
  const existingByDraft = {};
  (existingLines || []).forEach((row) => {
    if (row?.isDraft && row.draftTempId) existingByDraft[row.draftTempId] = row;
    else if (row?.product) existingByProduct[row.product] = row;
  });

  return [
    ...(selectedProductIds || []).map((productId) =>
      fillLineFromCatalog(
        existingByProduct[productId] || { product: productId, priority: false },
        (products || []).find((p) => p.id === productId),
      ),
    ),
    ...(draftProducts || []).map(
      (draft) =>
        existingByDraft[draft.tempId] || {
          product: null,
          isDraft: true,
          draftTempId: draft.tempId,
          draftProductName: draft.productName,
          draftProductCode: draft.productCode,
          draftProductBarcode: draft.productBarcode,
          price: 0,
          amount: 0,
          priority: false,
        },
    ),
  ];
};
